import { PostCard } from '../../component/groups/post-card'
import { PageContainer } from 'src/component/page-container/page-container'
import { Button } from '../../component/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../component/ui/dialog'
import { PageTitle } from '../../component/ui/page-title'
import { ImageFileId } from '../../component/image/image-file-id'
import { Plus } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { GroupMemberRole, GroupVisibility, GroupPostType } from 'src/types/groups'
import {
  useCreateGroupPost,
  useDeleteGroupPost,
  useGroup,
  useGroupMembers,
  useGroupPosts,
  useLeaveGroup,
} from 'src/hook/use-groups'
import { isFeatureFlagged } from 'src/util/feature-flags'
import { stringUtil } from 'src/util/string-util'
import { toasterUtil } from 'src/util/toaster-util'
import { useSessionContext } from 'src/context/session-context'

export const GroupDetailScreen = (): React.ReactElement => {
  const { groupId } = useParams<{ groupId: string }>()
  const navigate = useNavigate()
  const { me } = useSessionContext()

  useEffect(() => {
    if (isFeatureFlagged('GROUPS')) {
      navigate('/', { replace: true })
    }
  }, [navigate])

  const [postText, setPostText] = useState('')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showCreatePostModal, setShowCreatePostModal] = useState(false)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const { data: group, isLoading: isLoadingGroup } = useGroup(groupId!)
  const {
    data: postsData,
    isLoading: isLoadingPosts,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGroupPosts(groupId!, { limit: 20 })
  const { data: members = [], isLoading: isLoadingMembers } = useGroupMembers(groupId!, { limit: 10 })

  const createPostMutation = useCreateGroupPost()
  const deletePostMutation = useDeleteGroupPost()
  const leaveGroupMutation = useLeaveGroup()

  const handleCreatePost = async (): Promise<void> => {
    if (!postText.trim() || !groupId) return

    try {
      await createPostMutation.mutateAsync({
        groupId,
        input: {
          text: postText,
          postType: GroupPostType.DISCUSSION,
        },
      })
      setPostText('')
      setShowCreatePostModal(false)
      toasterUtil.success('Post created successfully')
    } catch {
      toasterUtil.error('Failed to create post')
    }
  }

  const handleDeletePost = async (postId: string): Promise<void> => {
    if (!groupId) return
    try {
      await deletePostMutation.mutateAsync({ groupId, postId })
      toasterUtil.success('Post deleted')
    } catch {
      toasterUtil.error('Failed to delete post')
    }
  }

  const handleLeaveGroup = async (): Promise<void> => {
    if (!groupId) return
    if (!window.confirm('Are you sure you want to leave this group?')) return

    try {
      await leaveGroupMutation.mutateAsync(groupId)
      toasterUtil.success('Left group successfully')
      navigate('/groups')
    } catch {
      toasterUtil.error('Failed to leave group')
    }
  }

  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 },
    )

    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  if (isLoadingGroup) {
    return (
      <PageContainer>
        <div className="py-6 md:py-[82px]">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded w-1/3 mb-6"></div>
            <div className="h-32 bg-gray-700 rounded mb-6"></div>
          </div>
        </div>
      </PageContainer>
    )
  }

  if (!group) {
    return (
      <PageContainer>
        <div className="py-6 md:py-[82px]">
          <PageTitle>Group Not Found</PageTitle>
        </div>
      </PageContainer>
    )
  }

  const posts = postsData?.pages.flatMap((page) => page.data) ?? []

  return (
    <PageContainer>
      <div className="py-6 md:py-[82px]">
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <PageTitle>{group.name}</PageTitle>
            {group.visibility === GroupVisibility.PRIVATE ? (
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
                />
              </svg>
            )}
          </div>
          {group.description && <p className="text-gray-400 mt-2">{group.description}</p>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {/* Posts Feed */}
            {isLoadingPosts ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-dark-gray rounded-lg p-6 animate-pulse">
                    <div className="h-4 bg-gray-700 rounded w-1/4 mb-4"></div>
                    <div className="h-20 bg-gray-700 rounded"></div>
                  </div>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20 bg-dark-gray rounded-lg">
                <p className="text-gray-400">No posts yet. Be the first to post!</p>
              </div>
            ) : (
              <>
                {posts.map((post) => {
                  const canDelete =
                    post.hustlerId === me?.id || group.myRole === GroupMemberRole.ADMIN || group.myRole === GroupMemberRole.OWNER
                  return <PostCard key={post.id} post={post} onDelete={canDelete ? handleDeletePost : undefined} />
                })}

                {/* Infinite scroll trigger */}
                {hasNextPage && (
                  <div ref={loadMoreRef} className="py-8 text-center">
                    {isFetchingNextPage && (
                      <div className="inline-flex items-center gap-2 text-white/60">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>Loading more posts...</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-3">
            <button
              onClick={() => setShowCreatePostModal(true)}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary/80 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Post
            </button>

            {group.inviteCode && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="w-full rounded-lg border border-white/10 px-4 py-3 text-sm font-semibold text-white hover:border-white/20 transition-colors"
              >
                Invite Members
              </button>
            )}

            {/* Members */}
            <div className="rounded-xl bg-white/5 p-5">
              <h3 className="text-ivory font-evogria text-lg mb-1">{group.memberCount ?? members.length} members</h3>
              {isLoadingMembers ? (
                <div className="space-y-3 mt-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 animate-pulse">
                      <div className="w-9 h-9 rounded-full bg-white/10"></div>
                      <div className="h-3 bg-white/10 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3 mt-4">
                  {members.slice(0, 5).map((member) => {
                    const displayName = stringUtil.getDisplayName(member.hustler ?? null)

                    return (
                      <div key={member.id} className="flex items-center gap-3">
                        <div className="flex w-9 h-9 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white">
                          {member.hustler?.profile?.profilePhotoFileId ? (
                            <ImageFileId
                              fileId={member.hustler.profile.profilePhotoFileId}
                              className="w-9 h-9 rounded-full object-cover"
                            />
                          ) : (
                            stringUtil.initials(displayName ?? '')
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-ivory text-sm truncate">{displayName}</p>
                          <p className="text-white/40 text-xs capitalize">{member.role}</p>
                        </div>
                      </div>
                    )
                  })}
                  {members.length > 5 && <p className="text-white/40 text-sm text-center mt-3">+{members.length - 5} more</p>}
                </div>
              )}
            </div>

            {/* Leave Group */}
            <button onClick={handleLeaveGroup} className="text-xs text-white/30 hover:text-white/60 transition-colors">
              Leave group
            </button>
          </div>
        </div>
      </div>

      {/* Create Post Modal */}
      <Dialog open={showCreatePostModal} onOpenChange={setShowCreatePostModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-ivory font-evogria">Create Post</DialogTitle>
          </DialogHeader>
          <textarea
            className="w-full resize-none rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-primary transition-colors"
            placeholder="What's on your mind?"
            value={postText}
            onChange={(e) => setPostText(e.target.value)}
            disabled={createPostMutation.isLoading}
            rows={4}
            autoFocus
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/30">{postText.length}/500</span>
            <Button onClick={handleCreatePost} disabled={!postText.trim() || createPostMutation.isLoading}>
              {createPostMutation.isLoading ? 'Posting...' : 'Post'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-ivory font-evogria">Invite Members</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/60">Share this link to invite people to {group.name}.</p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={`${window.location.origin}/groups/join?code=${group.inviteCode}`}
              className="flex-1 rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-ivory focus:outline-none"
            />
            <Button
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/groups/join?code=${group.inviteCode}`)
                toasterUtil.success('Invite link copied!')
              }}
            >
              Copy
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
